import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SEO, breadcrumbSchema, faqSchema, localBusinessSchema } from '../components/SEO';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FAQSection from '../components/FAQSection';
import { Reveal, Stagger, StaggerItem } from '../components/Reveal';
import { ProjectCard, ServiceCard } from '../components/cards';
import { getService, SERVICES } from '../data/services';
import { SERVICE_CITIES } from '../data/serviceCities';
import { PROJECTS } from '../data/projects';
import { LOCATIONS } from '../data/locations';
import { SITE, PROCESS_STEPS } from '../data/site';
import NotFoundPage from './NotFoundPage';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <NotFoundPage />;

  const related = service.related.map(getService).filter(Boolean);
  const relatedProjects = PROJECTS.filter((p) => p.serviceSlug === service.slug);
  const featuredAreas = LOCATIONS.slice(0, 5);

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.metaDescription,
    provider: { '@type': 'HomeAndConstructionBusiness', name: SITE.name, telephone: SITE.phone },
    areaServed: 'Omaha & Lincoln, Nebraska metro areas',
    url: `${SITE.baseUrl}/services/${service.slug}/`,
  };

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        path={`/services/${service.slug}/`}
        image={service.image.src}
        jsonLd={[
          serviceSchema,
          faqSchema(service.faqs),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Services', path: '/services/' }, { name: service.navTitle, path: `/services/${service.slug}/` }]),
        ]}
      />
      <PageHero
        overline={service.category}
        title={service.title}
        intro={service.excerpt}
        image={service.image}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Services', path: '/services/' }, { name: service.navTitle }]}
      />

      <section className="py-20 md:py-24">
        <div className="container-site grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Designed around the way you live</h2>
              {service.intro.map((p, i) => (
                <p key={i} className="mt-5 text-base leading-relaxed text-muted2 md:text-lg">{p}</p>
              ))}
            </Reveal>
            <Reveal className="mt-12">
              <h3 className="font-serif text-2xl font-medium tracking-tight text-ink">{service.options.title}</h3>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {service.options.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 border border-line bg-paper2/40 px-4 py-3.5 text-sm text-ink/80">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-bronze" /> {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-line bg-graphite p-8 text-paper noise-dark relative">
                <h3 className="relative z-10 font-serif text-2xl font-medium">Why homeowners choose us</h3>
                <ul className="relative z-10 mt-6 space-y-4">
                  {service.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-paper/75">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-bronze" /> {b}
                    </li>
                  ))}
                </ul>
                <Link to="/get-a-quote/" data-testid={`service-${service.slug}-quote-btn`} className="btn-primary relative z-10 mt-8 w-full">Get a Free Quote</Link>
                <p className="relative z-10 mt-4 text-center text-xs text-paper/50">Free on-site consultation · {SITE.phone}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-paper2/50 py-20 md:py-24">
        <div className="container-site">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">How your project unfolds</h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((s) => (
              <StaggerItem key={s.num}>
                <div className="border-t-2 border-bronze pt-5">
                  <span className="font-serif text-4xl font-light italic text-bronze">{s.num}</span>
                  <h3 className="mt-3 font-serif text-xl font-medium text-ink">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted2">{s.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {relatedProjects.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="container-site">
            <Reveal>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Related projects</h2>
            </Reveal>
            <Stagger className="mt-10 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {relatedProjects.map((p) => <StaggerItem key={p.slug}><ProjectCard project={p} /></StaggerItem>)}
            </Stagger>
          </div>
        </section>
      )}

      <section className="py-6 md:py-10">
        <div className="container-site max-w-4xl">
          <Reveal><FAQSection faqs={service.faqs} heading={`${service.navTitle} FAQs`} /></Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container-site grid gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">Related services</h2>
            <div className="mt-8 grid gap-5">
              {related.map((r) => <ServiceCard key={r.slug} service={r} />)}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">Serving your community</h2>
            <ul className="mt-8 divide-y divide-line border-y border-line">
              {featuredAreas.map((l) => (
                <li key={l.slug}>
                  <Link to={`/service-areas/${l.slug}/`} data-testid={`service-area-link-${l.slug}`} className="group flex items-center justify-between py-4">
                    <span className="font-serif text-xl text-ink transition-colors group-hover:text-bronze">{l.city}, Nebraska</span>
                    <ArrowRight className="h-4 w-4 text-bronze transition-transform group-hover:translate-x-1" />
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/service-areas/" data-testid="service-all-areas-link" className="link-underline mt-6 text-sm uppercase tracking-[0.2em]">All Service Areas <ArrowRight className="h-4 w-4" /></Link>
            {SERVICE_CITIES.filter((sc) => sc.serviceSlug === service.slug).length > 0 && (
              <div className="mt-8 border-t border-line pt-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-bronze">City-Specific Guides</p>
                <ul className="mt-3 space-y-2">
                  {SERVICE_CITIES.filter((sc) => sc.serviceSlug === service.slug).map((sc) => (
                    <li key={sc.path}>
                      <Link to={sc.path} data-testid={`service-city-link-${sc.slug}`} className="text-sm font-medium text-ink/80 underline decoration-line underline-offset-4 transition-colors hover:text-bronze">
                        {sc.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <CTASection title={`Ready to talk ${service.navTitle.toLowerCase()}?`} text="Start with a free on-site consultation. We'll listen first, then design something built around the way you live." testId={`service-${service.slug}-cta`} />
    </>
  );
};

export default ServiceDetailPage;
